'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { financialDataService, type CityRate } from '@/lib/services/financialDataService';

export default function CityGoldRates() {
  const [cityRates, setCityRates] = useState<CityRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const data = await financialDataService.getCityRates();
        setCityRates(data);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load city rates');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <section className="mb-12">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading city rates...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="mb-12">
        <div className="flex items-center justify-center py-12">
          <div className="text-secondary">Error: {error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="MapPinIcon" size={28} className="text-accent" />
        <h2 className="text-2xl font-bold text-foreground font-display">
          City-wise Gold & Silver Rates
        </h2>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">
                  City
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                  Gold 22K (10g)
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                  Gold 24K (10g)
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">
                  Silver (1kg)
                </th>
              </tr>
            </thead>
            <tbody>
              {cityRates?.map((city) => (
                <tr
                  key={city.id}
                  className="border-t border-border hover:bg-muted/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <span className="font-semibold text-foreground">{city.city}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div>
                      <span className="font-mono text-foreground font-semibold block mb-1">
                        ₹{city.gold22k.toLocaleString('en-IN')}
                      </span>
                      <span
                        className={`text-xs font-mono ${
                          city.change22k >= 0 ? 'text-primary' : 'text-secondary'
                        }`}
                      >
                        {city.change22k >= 0 ? '+' : ''}₹{city.change22k}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div>
                      <span className="font-mono text-foreground font-semibold block mb-1">
                        ₹{city.gold24k.toLocaleString('en-IN')}
                      </span>
                      <span
                        className={`text-xs font-mono ${
                          city.change24k >= 0 ? 'text-primary' : 'text-secondary'
                        }`}
                      >
                        {city.change24k >= 0 ? '+' : ''}₹{city.change24k}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div>
                      <span className="font-mono text-foreground font-semibold block mb-1">
                        ₹{city.silver.toLocaleString('en-IN')}
                      </span>
                      <span
                        className={`text-xs font-mono ${
                          city.changeSilver >= 0 ? 'text-primary' : 'text-secondary'
                        }`}
                      >
                        {city.changeSilver >= 0 ? '+' : ''}₹{city.changeSilver}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}